import React, { useCallback, useMemo, useState, useEffect} from "react";
import { useTranslation } from "react-i18next";
import { usePrefetch } from "../../utils/hooks/usePrefetch";

// Components
import Button from '../../components/CustomButton'
import GenerateModal from '../Modal';
import Star1 from '../../components/Svgs/Star1'
import Star2 from '../../components/Svgs/Star2'
import ProcessingModal from "../../components/ProcessingModal";
import QuizSetupModal from "../../components/QuizSetupModal";

// Styles
import styles from "./style.module.scss";

// Types
import { Material, Note, CardSet, Quiz, QuizSetupData } from "../../types";
import { GenerateType } from "../types";

// Assets
import VideoPng from 'images/icons/video.png'
import PodcastPng from 'images/icons/podcast.png'
import QuizPng from 'images/icons/quiz.png'
import FlashcardPng from 'images/icons/cards.png'

// Utils
import * as urls from "../../utils/urls";
import useIsDarkMode from "../../utils/hooks/darkMode";
import ThemeProvider from "../../utils/theme";
import useCsrfToken from "../../utils/hooks/csfrToken";
import FlashcardSetupModal from "../../components/FlashcardSetupModal/FlashcardSetupModal";
import globalModalManager from "../../utils/globalModalManager";

interface NoteGenerateButtonsProps {
  material: Material;
  note: Note;
  card_set: CardSet;
  quiz: Quiz;
  showOnlyQuizAndCards?: boolean;
  languages_select_options: Record<string, string>;
  showModalTabs?: boolean;
}

export const ButtonBackground: React.FC = () => {
  // Hooks
  const isDarkMode = useIsDarkMode();

  return (
    <>
      <Star1 className={styles.star1} opacity={isDarkMode ? 0.05 : 0.5} />
      <Star2 className={styles.star2} opacity={isDarkMode ? 0.05 : 0.5} />
    </>
  )
}

const NoteGenerateButtons: React.FC<NoteGenerateButtonsProps> = ({ material, note, card_set, quiz, showOnlyQuizAndCards = false, languages_select_options, showModalTabs = true }) => {
  // Translations
  const { t } = useTranslation();
  // Hooks
  const isDarkMode = useIsDarkMode();
  const csrfToken = useCsrfToken();
  const prefetch = usePrefetch();
  // States
  const [showQuizSetupModal, setShowQuizSetupModal] = useState<boolean>(false);
  const [showFlashcardSetupModal, setShowFlashcardSetupModal] = useState<boolean>(false);
  const [generateModal, setGenerateModal] = useState<GenerateType | null>(null);

  const [processing, setProcessing] = useState<boolean>(false);

  useEffect(() => {
    const handleQuizTrigger = (event: CustomEvent) => {
      if (!event.detail?.materialId || event.detail.materialId !== material.id.toString()) return;
      handleQuiz();
    };
    const handleFlashcardTrigger = (event: CustomEvent) => {
      if (!event.detail?.materialId || event.detail.materialId !== material.id.toString()) return;
      handleFlashcard();
    };
    const handleVideoTrigger = () => { handleGenerateVideo() };
    const handlePodcastTrigger = () => { handleGeneratePodcast() };

    window.addEventListener('openQuizSetupModal', handleQuizTrigger as EventListener);
    window.addEventListener('openFlashcardSetupModal', handleFlashcardTrigger as EventListener);
    window.addEventListener('openVideoModal', handleVideoTrigger);
    window.addEventListener('openPodcastModal', handlePodcastTrigger);
    return () => {
      window.removeEventListener('openQuizSetupModal', handleQuizTrigger as EventListener);
      window.removeEventListener('openFlashcardSetupModal', handleFlashcardTrigger as EventListener);
      window.removeEventListener('openVideoModal', handleVideoTrigger);
      window.removeEventListener('openPodcastModal', handlePodcastTrigger);
    };
  }, []);

  // Memoized Values
  const hasQuiz = useMemo(() => quiz && quiz.id, [quiz]);
  const hasFlashcard = useMemo(() => card_set && card_set.id, [card_set]);

  // Handlers
  const handleGenerateVideo = useCallback(() => {
    globalModalManager.openModal('available-soon');
  }, []);
  const handleGeneratePodcast = useCallback(() => {
    globalModalManager.openModal('available-soon');
    // Locked for now
    // setGenerateModal('podcast');
  }, [])

  const handleGenerateQuiz = useCallback(async (data: QuizSetupData) => {
    const { timeLimit, questions, adaptiveMix, skippingHints, max_questions, language } = data;
    setShowQuizSetupModal(false);
    setProcessing(true);

    try {
      const formData = new FormData();
      formData.append('quiz[language]', language || 'en')
      formData.append('quiz[difficulty]', 'easy')
      formData.append('quiz[max_questions]', max_questions.toString())

      if (timeLimit) {
        formData.append('quiz[time_limit]', timeLimit.toString());
      }


      questions.forEach((question, index) => {
        formData.append(`quiz[questions][${index}][question_type]`, question.question_type);
        formData.append(`quiz[questions][${index}][qty]`, question.qty.toString());
      });

      formData.append('quiz[adaptive_mix]', adaptiveMix.toString());
      formData.append('quiz[skipping_hints]', skippingHints.toString());

      const url = urls.aiQuizzesUrl(material.id);
      const options = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: formData,
      }
      const response = await fetch(url, options);
      if (response.ok) {
        const quiz = await response.json();
        Turbo.visit(urls.quizUrl(quiz.data.id));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setProcessing(false);
    }
  }, [csrfToken]);

  const handleQuiz = useCallback(() => {
    if (!hasQuiz) {
      setShowQuizSetupModal(true);
    } else {
      window.Turbo.visit(urls.quizUrl(quiz.id));
    }
  }, [hasQuiz]);

  const handleFlashcard = useCallback(() => {
    if (!hasFlashcard) {
      setShowFlashcardSetupModal(true);
    } else {
      window.Turbo.visit(urls.cardSetSlidesUrl(card_set.id));
    }
  }, [hasFlashcard]);

  return (
    <ThemeProvider>
      <div className={styles.root}>
        {showOnlyQuizAndCards && (
          <div className={styles.row}>
            <Button
              variant="primary"
              classes={{ root: `${styles.actionButton} ${styles.quiz}` }}
              onClick={handleQuiz}
              onMouseEnter={() => hasQuiz && prefetch(urls.quizUrl(quiz.id), 'mouseenter')}
              onTouchStart={() => hasQuiz && prefetch(urls.quizUrl(quiz.id), 'touchstart')}
            >
              <ButtonBackground />

              <p className={styles.actionButtonContent}>
                <img src={QuizPng} alt="Quiz" className={styles.actionButtonIcon} />
                <span>
                  <span> {hasQuiz ? t('generate.actions.show') : t('generate.actions.make')}</span>
                  <span> {t('generate.actions.quiz')} </span>
                </span>
              </p>
            </Button>
            <Button
              variant="primary"
              classes={{ root: `${styles.actionButton} ${styles.flashcard}` }}
              onClick={handleFlashcard}
              onMouseEnter={() => hasFlashcard && prefetch(urls.cardSetSlidesUrl(card_set.id), 'mouseenter')}
              onTouchStart={() => hasFlashcard && prefetch(urls.cardSetSlidesUrl(card_set.id), 'touchstart')}
            >
              <ButtonBackground />

              <p className={styles.actionButtonContent}>
                <img src={FlashcardPng} alt="Flashcard" className={styles.actionButtonIcon} />
                <span>
                  <span> {hasFlashcard ? t('generate.actions.show') : t('generate.actions.make')}</span>
                  <span> {t('generate.actions.flashcard')} </span>
                </span>
              </p>
            </Button>
          </div>
        )}

        <QuizSetupModal
          allowFileUpload={false}
          open={showQuizSetupModal}
          onClose={() => setShowQuizSetupModal(false)}
          onGenerate={handleGenerateQuiz}
          languages={languages_select_options}
          showModalTabs={showModalTabs}
        />

        <FlashcardSetupModal
          smartNoteIdscope={[note.id]}
          open={showFlashcardSetupModal}
          onClose={() => setShowFlashcardSetupModal(false)}
          languages={languages_select_options}
        />

        <ProcessingModal open={processing} />

        <GenerateModal
          open={!!generateModal}
          onClose={() => setGenerateModal(null)}
          type={generateModal}
        />
      </div>
    </ThemeProvider>
  );
};

export default NoteGenerateButtons;
